import React, { ReactNode, useCallback, useContext, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import DownloadIcon from 'icons/Download.icon';
import EyeIcon from 'icons/Eye.icon';
import utils, { getHasElementsInArray, getUserFullName } from 'utils';
import { Loader } from 'ncoded-component-library';
import ReportsEmptyState from '../../../Reports/components/ReportsEmptyState';
import { isDataRoomViewer } from 'router/subrouters/DataRoom/utils';
import LockclosedIcon from 'icons/Lockclosed.icon';
import { SortingButton } from 'router/subrouters/DataRoom/components/SortingButton';
import usePastTime from 'hooks/usePastTime';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import useRankedMembersQuery from './hooks/useRankedMembersQuery';
import TimelineTag from '../TimelineTag';

import './RankedMembers.styles.scss';

type SortName =
  | 'numberOfViews'
  | 'numberOfDownloads'
  | 'numberOfSecondsSpent'
  | 'updatedAt';

const POPUP_CONTENT = {
  numberOfViews: 'numberOfViewsPopup',
  numberOfDownloads: 'numberOfDownloadsPopup',
  numberOfSecondsSpent: 'numberOfSecondsSpentPopup',
  updatedAt: 'updatedAtPopup',
};

type RankedMembersProps = {
  className?: string;
};

const RankedMembers: React.FC<RankedMembersProps> = (props) => {
  const { className } = props;

  const [sort, setSort] = useState('-numberOfDownloads');

  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);

  const pastTime = usePastTime();

  const { t } = useTranslation();

  const { reports, isLoading } = useRankedMembersQuery({
    dataRoomId,
    sort,
  });

  const classes = classNames(
    'ranked-members',
    { loading: isLoading },
    className,
  );

  const renderTrackedTime = useCallback(
    (seconds: number) => {
      const { type, count } = utils.timeSpentInSecondsHumanize(seconds);
      return t(`timeSpentInSecondsHumanize.${type}`, { count });
    },
    [t],
  );

  const renderSortButton = useCallback(
    (sortName: SortName, content?: ReactNode) => (
      <SortingButton
        content={content}
        sortingValue={sortName}
        popupContent={t(POPUP_CONTENT[sortName])}
        sort={sort}
        updateSort={setSort}
      />
    ),
    [sort, t],
  );

  return (
    <div className={classes}>
      {isLoading && <Loader />}

      <div className="header">
        <p>{t('topRankedMember')}</p>
        <TimelineTag />
      </div>

      {!getHasElementsInArray(reports) ? (
        isLoading ? (
          <Loader overlay={false} />
        ) : (
          <ReportsEmptyState />
        )
      ) : (
        <div className="ranked-members__scrolling-container">
          <table>
            <thead>
              <tr>
                <th>{t('member')}</th>
                <th>{renderSortButton('numberOfDownloads', t('downloads'))}</th>
                <th>{renderSortButton('numberOfViews', t('views'))}</th>
                <th>{renderSortButton('numberOfSecondsSpent', t('time'))}</th>
                <th>{renderSortButton('updatedAt', t('recent'))}</th>
              </tr>
            </thead>
            <tbody>
              {reports.map(
                ({
                  _id,
                  dataRoomMember,
                  numberOfDownloads,
                  numberOfViews,
                  numberOfSecondsSpent,
                  updatedAt,
                }) => (
                  <tr key={_id}>
                    <td>
                      <div className="ranked-members__name">
                        <span>
                          {getUserFullName(
                            dataRoomMember?.firstName,
                            dataRoomMember?.lastName,
                          )}
                        </span>
                      </div>
                    </td>
                    <td
                      className={classNames('downloads', {
                        selected: sort === 'numberOfDownloads',
                      })}
                    >
                      <div>
                        {isDataRoomViewer(dataRoomMember?.role) ? (
                          <LockclosedIcon />
                        ) : (
                          <>
                            {numberOfDownloads}
                            <DownloadIcon />
                          </>
                        )}
                      </div>
                    </td>
                    <td
                      className={classNames({
                        selected: sort === 'numberOfViews',
                      })}
                    >
                      <div>
                        {numberOfViews}
                        <EyeIcon />
                      </div>
                    </td>
                    <td
                      className={classNames({
                        selected: sort === 'numberOfSecondsSpent',
                      })}
                    >
                      <div>{renderTrackedTime(numberOfSecondsSpent)}</div>
                    </td>
                    <td
                      className={classNames({
                        selected: sort === 'updatedAt',
                      })}
                    >
                      <span>{pastTime(updatedAt)}</span>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RankedMembers;
